import { useCallback, useEffect, useRef, type MutableRefObject } from 'react';
import { supabase } from '../utils/supabase/client';
import { devLog, devError, devWarn } from '../utils/logger';

type UserSyncOptions = {
  session: any;
  enabled?: boolean;
  onProfileRefresh?: () => void;
  onSubscriptionRefresh?: () => void;
  onBlockedUsersRefresh?: () => void;
  onGigsRefresh?: () => void;
  onGigInterestsRefresh?: () => void;
};

/**
 * Cross-device user sync via Supabase Realtime with lightweight fallback polling.
 */
export function useUserSync({
  session,
  enabled = true,
  onProfileRefresh,
  onSubscriptionRefresh,
  onBlockedUsersRefresh,
  onGigsRefresh,
  onGigInterestsRefresh,
}: UserSyncOptions) {
  const onProfileRefreshRef = useRef(onProfileRefresh);
  const onSubscriptionRefreshRef = useRef(onSubscriptionRefresh);
  const onBlockedUsersRefreshRef = useRef(onBlockedUsersRefresh);
  const onGigsRefreshRef = useRef(onGigsRefresh);
  const onGigInterestsRefreshRef = useRef(onGigInterestsRefresh);

  useEffect(() => {
    onProfileRefreshRef.current = onProfileRefresh;
    onSubscriptionRefreshRef.current = onSubscriptionRefresh;
    onBlockedUsersRefreshRef.current = onBlockedUsersRefresh;
    onGigsRefreshRef.current = onGigsRefresh;
    onGigInterestsRefreshRef.current = onGigInterestsRefresh;
  }, [
    onProfileRefresh,
    onSubscriptionRefresh,
    onBlockedUsersRefresh,
    onGigsRefresh,
    onGigInterestsRefresh,
  ]);

  const profileRefreshTimerRef = useRef<number | null>(null);
  const subscriptionRefreshTimerRef = useRef<number | null>(null);
  const blockedRefreshTimerRef = useRef<number | null>(null);
  const gigsRefreshTimerRef = useRef<number | null>(null);
  const gigInterestsRefreshTimerRef = useRef<number | null>(null);
  const fallbackPollingTimerRef = useRef<number | null>(null);
  const fallbackPollDelayMsRef = useRef(30000);
  const lastFallbackRefreshAtRef = useRef(0);
  const realtimeDegradedRef = useRef(false);
  const lastProfileRefreshAtRef = useRef(0);
  const lastSubscriptionRefreshAtRef = useRef(0);
  const lastBlockedRefreshAtRef = useRef(0);
  const lastGigsRefreshAtRef = useRef(0);
  const lastGigInterestsRefreshAtRef = useRef(0);
  const lastVisibilityRefreshAtRef = useRef(0);
  const lastOnlineRefreshAtRef = useRef(0);
  const syncStatsRef = useRef({
    refreshScheduled: 0,
    refreshExecuted: 0,
    refreshErrors: 0,
    fallbackStarts: 0,
    fallbackStops: 0,
    channelSubscribed: 0,
    channelTimeouts: 0,
    channelErrors: 0,
    channelClosed: 0,
  });

  const scheduleRefresh = useCallback(
    (
      reason: string,
      callback: (() => void | Promise<void>) | undefined,
      timerRef: MutableRefObject<number | null>,
      lastRefreshAtRef: MutableRefObject<number>
    ) => {
      if (!callback) return;

      const now = Date.now();
      const elapsed = now - lastRefreshAtRef.current;
      const MIN_REFRESH_GAP_MS = 700;

      // Skip if we just refreshed very recently (event bursts are common).
      if (elapsed < MIN_REFRESH_GAP_MS) return;

      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      syncStatsRef.current.refreshScheduled += 1;
      timerRef.current = window.setTimeout(async () => {
        try {
          lastRefreshAtRef.current = Date.now();
          syncStatsRef.current.refreshExecuted += 1;
          devLog('🔄 useUserSync refresh:', reason);
          await callback();
        } catch (error) {
          syncStatsRef.current.refreshErrors += 1;
          devError('useUserSync refresh failed:', error);
        } finally {
          timerRef.current = null;
        }
      }, 150);
    },
    []
  );

  const scheduleProfileRefresh = useCallback(
    (reason: string) => {
      scheduleRefresh(reason, onProfileRefreshRef.current, profileRefreshTimerRef, lastProfileRefreshAtRef);
    },
    [scheduleRefresh]
  );

  const scheduleSubscriptionRefresh = useCallback(
    (reason: string) => {
      scheduleRefresh(
        reason,
        onSubscriptionRefreshRef.current ?? onProfileRefreshRef.current,
        subscriptionRefreshTimerRef,
        lastSubscriptionRefreshAtRef
      );
    },
    [scheduleRefresh]
  );

  const scheduleBlockedRefresh = useCallback(
    (reason: string) => {
      scheduleRefresh(
        reason,
        onBlockedUsersRefreshRef.current ?? onProfileRefreshRef.current,
        blockedRefreshTimerRef,
        lastBlockedRefreshAtRef
      );
    },
    [scheduleRefresh]
  );

  const scheduleGigsRefresh = useCallback(
    (reason: string) => {
      scheduleRefresh(reason, onGigsRefreshRef.current, gigsRefreshTimerRef, lastGigsRefreshAtRef);
    },
    [scheduleRefresh]
  );

  const scheduleGigInterestsRefresh = useCallback(
    (reason: string) => {
      scheduleRefresh(
        reason,
        onGigInterestsRefreshRef.current ?? onGigsRefreshRef.current,
        gigInterestsRefreshTimerRef,
        lastGigInterestsRefreshAtRef
      );
    },
    [scheduleRefresh]
  );

  useEffect(() => {
    if (!enabled || !session?.user?.id) return;
    const userId = session.user.id as string;
    const shouldSyncProfile = !!onProfileRefreshRef.current;
    const shouldSyncSubscription = !!(onSubscriptionRefreshRef.current ?? onProfileRefreshRef.current);
    const shouldSyncBlocked = !!(onBlockedUsersRefreshRef.current ?? onProfileRefreshRef.current);
    const shouldSyncGigs = !!onGigsRefreshRef.current;
    const shouldSyncGigInterests = !!(onGigInterestsRefreshRef.current ?? onGigsRefreshRef.current);
    const needsRealtime =
      shouldSyncProfile ||
      shouldSyncSubscription ||
      shouldSyncBlocked ||
      shouldSyncGigs ||
      shouldSyncGigInterests;
    if (!needsRealtime) return;

    const runFallbackRefresh = (reason: string) => {
      const now = Date.now();
      // Dedupe fallback bursts that can happen on channel flaps.
      if (now - lastFallbackRefreshAtRef.current < 5000) return;
      lastFallbackRefreshAtRef.current = now;
      if (shouldSyncProfile) scheduleProfileRefresh(`fallback:${reason}`);
      if (shouldSyncSubscription) scheduleSubscriptionRefresh(`fallback:${reason}`);
      if (shouldSyncBlocked) scheduleBlockedRefresh(`fallback:${reason}`);
      if (shouldSyncGigs) scheduleGigsRefresh(`fallback:${reason}`);
      if (shouldSyncGigInterests) scheduleGigInterestsRefresh(`fallback:${reason}`);
    };

    const scheduleNextFallbackTick = () => {
      if (!realtimeDegradedRef.current) return;
      const jitterMs = Math.floor(Math.random() * 3000);
      const nextDelayMs = Math.min(fallbackPollDelayMsRef.current + jitterMs, 180000);
      fallbackPollingTimerRef.current = window.setTimeout(() => {
        if (document.visibilityState === 'visible') {
          runFallbackRefresh('poll');
        }
        fallbackPollDelayMsRef.current = Math.min(
          Math.round(fallbackPollDelayMsRef.current * 1.5),
          180000
        );
        scheduleNextFallbackTick();
      }, nextDelayMs);
    };

    const startFallbackPolling = (channel: string, reason: string) => {
      if (realtimeDegradedRef.current) return;
      realtimeDegradedRef.current = true;
      syncStatsRef.current.fallbackStarts += 1;
      devLog('⚠️ useUserSync realtime degraded - starting fallback polling:', `${channel}:${reason}`);
      fallbackPollDelayMsRef.current = 30000;
      runFallbackRefresh(`${channel}:${reason}`);
      if (fallbackPollingTimerRef.current !== null) {
        window.clearTimeout(fallbackPollingTimerRef.current);
      }
      scheduleNextFallbackTick();
    };

    const stopFallbackPolling = (channel: string, reason: string) => {
      if (!realtimeDegradedRef.current) return;
      realtimeDegradedRef.current = false;
      syncStatsRef.current.fallbackStops += 1;
      devLog('✅ useUserSync realtime recovered - stopping fallback polling:', `${channel}:${reason}`);
      if (fallbackPollingTimerRef.current !== null) {
        window.clearTimeout(fallbackPollingTimerRef.current);
        fallbackPollingTimerRef.current = null;
      }
    };

    const syncChannel = supabase.channel(`user-sync:${userId}`);

    if (shouldSyncProfile) {
      syncChannel.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${userId}`,
        },
        (payload: any) => {
          const oldRow = payload?.old || {};
          const newRow = payload?.new || {};
          const changedKeys = Object.keys(newRow).filter((key) => oldRow?.[key] !== newRow?.[key]);
          const ignorableOnly =
            changedKeys.length > 0 &&
            changedKeys.every((key) => key === 'updated_at' || key === 'last_active');

          if (!ignorableOnly) {
            scheduleProfileRefresh('profiles:UPDATE');
          }
        }
      );
    }

    if (shouldSyncSubscription) {
      syncChannel.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions',
          filter: `user_id=eq.${userId}`,
        },
        (payload: any) => {
          // Only refresh for meaningful subscription changes.
          // Ignore noisy metadata updates (e.g. updated_at/current_period_end sync).
          if (payload?.eventType === 'INSERT' || payload?.eventType === 'DELETE') {
            scheduleSubscriptionRefresh('subscriptions:*');
            return;
          }

          const oldRow = payload?.old || {};
          const newRow = payload?.new || {};
          const hasMeaningfulChange =
            oldRow.status !== newRow.status ||
            oldRow.plan_type !== newRow.plan_type ||
            oldRow.cancel_at_period_end !== newRow.cancel_at_period_end ||
            oldRow.stripe_subscription_id !== newRow.stripe_subscription_id;

          if (hasMeaningfulChange) {
            scheduleSubscriptionRefresh('subscriptions:*');
          }
        }
      );
    }

    if (shouldSyncBlocked) {
      syncChannel
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'blocked_users',
            filter: `blocker_id=eq.${userId}`,
          },
          () => {
            scheduleBlockedRefresh('blocked_users:blocker_id:*');
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'blocked_users',
            filter: `blocked_id=eq.${userId}`,
          },
          () => {
            scheduleBlockedRefresh('blocked_users:blocked_id:*');
          }
        );
    }

    if (shouldSyncGigs) {
      syncChannel.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'gigs',
          filter: `employer_id=eq.${userId}`,
        },
        () => {
          scheduleGigsRefresh('gigs:*');
        }
      );
    }

    if (shouldSyncGigInterests) {
      syncChannel
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'gig_interests',
            filter: `user_id=eq.${userId}`,
          },
          () => {
            scheduleGigInterestsRefresh('gig_interests:user_id:*');
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'gig_interests',
          },
          async (payload: any) => {
            const changedGigId = payload?.new?.gig_id || payload?.old?.gig_id;
            if (!changedGigId) return;

            try {
              const { data, error } = await supabase
                .from('gigs')
                .select('id')
                .eq('id', changedGigId)
                .eq('employer_id', userId)
                .maybeSingle();

              if (!error && data) {
                scheduleGigInterestsRefresh('gig_interests:employer_gig:*');
              }
            } catch {
              // no-op: best effort realtime for employer interests
            }
          }
        );
    }

    syncChannel.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        syncStatsRef.current.channelSubscribed += 1;
        devLog('✅ useUserSync channel subscribed');
        stopFallbackPolling('user-sync', 'SUBSCRIBED');
        return;
      }

      if (status === 'CHANNEL_ERROR') {
        syncStatsRef.current.channelErrors += 1;
        devError('❌ useUserSync channel error');
        startFallbackPolling('user-sync', 'CHANNEL_ERROR');
      }
      if (status === 'TIMED_OUT') {
        syncStatsRef.current.channelTimeouts += 1;
        devWarn('⏱️ useUserSync channel timed out');
        startFallbackPolling('user-sync', 'TIMED_OUT');
      }
      if (status === 'CLOSED') {
        syncStatsRef.current.channelClosed += 1;
        devWarn('🔒 useUserSync channel closed');
        startFallbackPolling('user-sync', 'CLOSED');
      }
    });

    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        if (now - lastVisibilityRefreshAtRef.current < 15000) return;
        lastVisibilityRefreshAtRef.current = now;
        const profileTarget = onProfileRefreshRef.current;
        const subscriptionTarget = onSubscriptionRefreshRef.current ?? profileTarget;
        const blockedTarget = onBlockedUsersRefreshRef.current ?? profileTarget;

        scheduleProfileRefresh('app:visible');
        if (subscriptionTarget && subscriptionTarget !== profileTarget) {
          scheduleSubscriptionRefresh('app:visible');
        }
        if (
          blockedTarget &&
          blockedTarget !== profileTarget &&
          blockedTarget !== subscriptionTarget
        ) {
          scheduleBlockedRefresh('app:visible');
        }
        scheduleGigsRefresh('app:visible');
        scheduleGigInterestsRefresh('app:visible');
      }
    };

    const handleOnline = () => {
      const now = Date.now();
      if (now - lastOnlineRefreshAtRef.current < 10000) return;
      lastOnlineRefreshAtRef.current = now;
      const profileTarget = onProfileRefreshRef.current;
      const subscriptionTarget = onSubscriptionRefreshRef.current ?? profileTarget;
      const blockedTarget = onBlockedUsersRefreshRef.current ?? profileTarget;

      scheduleProfileRefresh('network:online');
      if (subscriptionTarget && subscriptionTarget !== profileTarget) {
        scheduleSubscriptionRefresh('network:online');
      }
      if (
        blockedTarget &&
        blockedTarget !== profileTarget &&
        blockedTarget !== subscriptionTarget
      ) {
        scheduleBlockedRefresh('network:online');
      }
      scheduleGigsRefresh('network:online');
      scheduleGigInterestsRefresh('network:online');
    };

    document.addEventListener('visibilitychange', handleVisibilityOrFocus);
    window.addEventListener('focus', handleVisibilityOrFocus);
    window.addEventListener('online', handleOnline);
    const statsLogTimer = window.setInterval(() => {
      devLog('📈 useUserSync stats', { ...syncStatsRef.current });
    }, 60000);

    return () => {
      supabase.removeChannel(syncChannel);
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
      window.removeEventListener('focus', handleVisibilityOrFocus);
      window.removeEventListener('online', handleOnline);
      window.clearInterval(statsLogTimer);
      if (profileRefreshTimerRef.current !== null) {
        window.clearTimeout(profileRefreshTimerRef.current);
        profileRefreshTimerRef.current = null;
      }
      if (subscriptionRefreshTimerRef.current !== null) {
        window.clearTimeout(subscriptionRefreshTimerRef.current);
        subscriptionRefreshTimerRef.current = null;
      }
      if (blockedRefreshTimerRef.current !== null) {
        window.clearTimeout(blockedRefreshTimerRef.current);
        blockedRefreshTimerRef.current = null;
      }
      if (gigsRefreshTimerRef.current !== null) {
        window.clearTimeout(gigsRefreshTimerRef.current);
        gigsRefreshTimerRef.current = null;
      }
      if (gigInterestsRefreshTimerRef.current !== null) {
        window.clearTimeout(gigInterestsRefreshTimerRef.current);
        gigInterestsRefreshTimerRef.current = null;
      }
      if (fallbackPollingTimerRef.current !== null) {
        window.clearTimeout(fallbackPollingTimerRef.current);
        fallbackPollingTimerRef.current = null;
      }
      realtimeDegradedRef.current = false;
    };
  }, [
    enabled,
    session?.user?.id,
    scheduleProfileRefresh,
    scheduleSubscriptionRefresh,
    scheduleBlockedRefresh,
    scheduleGigsRefresh,
    scheduleGigInterestsRefresh,
  ]);
}
